import type { LucideIcon } from 'lucide-react';
import { type ReactNode, createContext, use } from 'react';
import { createPortal } from 'react-dom';

interface PageHeaderSlot {
  readonly target: HTMLElement | null;
  readonly title?: string | undefined;
  readonly icon?: LucideIcon | undefined;
  readonly insetClassName?: string | undefined;
}

interface PageHeaderProps {
  readonly icon?: LucideIcon | undefined;
  readonly title?: string | undefined;
  readonly description?: string | undefined;
  readonly actions?: ReactNode | undefined;
}

const PageHeaderSlotContext = createContext<PageHeaderSlot>({ target: null });

export function PageHeaderSlotProvider({
  slot,
  children,
}: {
  readonly slot: PageHeaderSlot;
  readonly children: ReactNode;
}): ReactNode {
  return <PageHeaderSlotContext value={slot}>{children}</PageHeaderSlotContext>;
}

function PageHeaderBar({
  icon: Icon,
  title,
  description,
  actions,
  insetClassName = 'px-4 sm:px-6 md:px-8 lg:px-16',
}: PageHeaderProps & { readonly title: string; readonly insetClassName?: string | undefined }) {
  return (
    <div
      className={`flex flex-col gap-4 md:flex-row md:items-center md:justify-between ${insetClassName} py-4 sm:py-5 bg-surface border-b border-border`}
    >
      <div className="flex items-start gap-3 min-w-0">
        {Icon && (
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-card border border-border shrink-0">
            <Icon size={18} className="text-foreground" />
          </div>
        )}
        <div className="flex flex-col gap-0.5 min-w-0">
          <h1 className="font-display text-xl sm:text-2xl font-semibold text-foreground truncate">
            {title}
          </h1>
          {description && <p className="text-xs sm:text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex gap-2 items-center shrink-0">{actions}</div>}
    </div>
  );
}

export function PageHeader({ icon, title, description, actions }: PageHeaderProps): ReactNode {
  const slot = use(PageHeaderSlotContext);
  const resolvedTitle = title ?? slot.title;
  if (!slot.target || !resolvedTitle) return null;

  return createPortal(
    <PageHeaderBar
      icon={icon ?? slot.icon}
      title={resolvedTitle}
      description={description}
      actions={actions}
      insetClassName={slot.insetClassName}
    />,
    slot.target,
  );
}
